import {
  SearchControllerSearchRequest,
  TrackControllerRecommendationSearchRequest,
} from '@cratehackers/api-client';
import { IonButton, IonCard, IonCardContent } from '@ionic/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ApiService from '../../../services/Api.service';
import {
  setSelectedTrack,
  setActiveRecommendation,
} from '../../../store/slices/track.slice';
import { AppDispatch, RootState } from '../../../store/store';
import { iTrack } from '../../../types';
import {
  SeedSearchFieldSkeleton,
  WidgetListRowsSkeleton,
} from '../../dashboard/HomepageSkeletons';
import CamelotKeyChip from '../CamelotKeyChip';
import SeedSearchField from '../SeedSearchField';
import './TrackRecommendationsWidget.scss';
import { cleanTitle, cleanArtist } from '../../../utils/cleanTrackMetadata';

type KeyCategory =
  | 'PerfectMatch'
  | 'EnergyBoost'
  | 'EnergyDrop'
  | 'MoodChange'
  | 'Compatible';

interface KeyMatches {
  perfectMatch?: string[];
  energyBoost?: string[];
  energyDrop?: string[];
  moodChange?: string[];
  compatible?: string[];
}

interface TrackInfo {
  id: string;
  title: string;
  artist: string;
  bpm: number;
  key: string;
  albumArt: string;
  previewUrl?: string;
}

type Recommendation = TrackInfo;

const keyCategoryOrder: Record<KeyCategory, number> = {
  PerfectMatch: 1,
  EnergyBoost: 2,
  EnergyDrop: 3,
  MoodChange: 4,
  Compatible: 5,
};

const keyCategoryColors: Record<KeyCategory, string> = {
  PerfectMatch: '#2fdf75',
  EnergyBoost: '#d3771c',
  EnergyDrop: '#50c8ff',
  MoodChange: '#ee7bb4',
  Compatible: 'rgba(255,255,255,0.5)',
};

const formatKeyCategory = (category: string): string => {
  if (category === 'PerfectMatch') return 'Perfect match';
  if (category === 'EnergyBoost') return 'Energy boost';
  if (category === 'EnergyDrop') return 'Energy drop';
  if (category === 'MoodChange') return 'Mood change';
  return 'Compatible';
};

export const TrackRecommendationsWidget: React.FC<{ editing: boolean }> = ({
  editing,
}) => {
  const [query, setQuery] = useState<string>('');
  const [seedTrack, setSeedTrack] = useState<TrackInfo | null>(null);
  const [searchResults, setSearchResults] = useState<TrackInfo[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [keyMatches, setKeyMatches] = useState<KeyMatches | null>(null);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const activeRecommendation = useSelector(
    (state: RootState) => state.track.activeRecommendation,
  );
  const history = useHistory();
  const hasFetchedInitialSong = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const defaultChartId = '1732910153124x930312595630456800'; //POP current

  // Fetch initial random song from chart on mount
  useEffect(() => {
    if (hasFetchedInitialSong.current) return;
    hasFetchedInitialSong.current = true;

    fetchInitialSong();
  }, []);

  const fetchInitialSong = async () => {
    try {
      const chAPI = ApiService.getApiClient();
      const chartData = await chAPI.chartControllerGetCHChart({
        id: defaultChartId,
      });
      if (chartData.tracks && chartData.tracks.length > 0) {
        const maxIndex = Math.min(10, chartData.tracks.length);
        const randomIndex = Math.floor(Math.random() * maxIndex);
        const track = chartData.tracks[randomIndex];
        await selectTrackAsSeed(`${track.artist} - ${track.name}`);
      } else {
        setError('No tracks found in chart.');
        setInitialLoading(false);
      }
    } catch {
      setError('Failed to load initial recommendations.');
      setInitialLoading(false);
    }
  };
  // Debounced search effect - fetches search results for dropdown
  useEffect(() => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || trimmedQuery.length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const handler = setTimeout(() => {
      fetchSearchResults(trimmedQuery);
    }, 400);

    return () => clearTimeout(handler);
  }, [query]);

  // Fetch search results for the dropdown using proper search API
  const fetchSearchResults = async (searchTerm: string) => {
    try {
      const api = ApiService.getApiClient();
      const req: SearchControllerSearchRequest = {
        searchDto: {
          term: searchTerm,
          objectType: 'track',
          offset: 0,
          additionalParams: { markets: [] },
          raw: false,
          limit: 8,
        },
      };
      const resp = await api.searchControllerSearch(req);

      if (!resp?.items || resp.items.length === 0) {
        setSearchResults([]);
        return;
      }

      // Map search results to TrackInfo format
      const results: TrackInfo[] = resp.items.map((t: any) => ({
        id: t.uuid || t.id || '',
        title: t.title || '',
        artist: t.artist?.name || '',
        albumArt: t.album?.picture || '',
        bpm: Math.round(t.bpm || 0),
        key: t.key || '',
        previewUrl: t.previewUrl,
      }));

      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Select a track as seed and fetch its recommendations
  const selectTrackAsSeed = async (searchTerm: string) => {
    try {
      setInitialLoading(true);
      setError(null);
      setSearchResults([]);
      setQuery('');

      const api = ApiService.getApiClient();
      const req: TrackControllerRecommendationSearchRequest = {
        spotifySearchDto: { term: searchTerm },
      };
      const resp = await api.trackControllerRecommendationSearch(req);

      if (!resp || resp.length === 0) {
        setError('No tracks found.');
        return;
      }

      // First track is the seed track
      const firstTrack = resp[0];
      const seed: TrackInfo = {
        id: firstTrack.id || '',
        title: firstTrack.name || '',
        artist: firstTrack.artists?.map((a: any) => a.name).join(', ') || '',
        albumArt: firstTrack.album?.imageUrl || '',
        bpm: Math.round(firstTrack.bpm || 0),
        key: firstTrack.keys?.camelot || '',
        previewUrl: firstTrack.previewUrl,
      };
      setSeedTrack(seed);

      // Fetch key matches for the seed track
      if (firstTrack.keys?.camelot) {
        try {
          const matches = await api.keysControllerGetKeyMatches({
            keyDto: {
              camelot: firstTrack.keys.camelot,
              standard: '',
            },
          });
          setKeyMatches(matches);
        } catch {
          setKeyMatches(null);
        }
      }

      // Map remaining tracks as recommendations
      const items: Recommendation[] = resp
        .slice(1)
        .map((t: any) => ({
          id: t.id,
          title: t.name,
          artist: t.artists?.map((a: any) => a.name).join(', ') || '',
          albumArt: t.album?.imageUrl || '',
          bpm: Math.round(t.bpm || 0),
          key: t.keys?.camelot || '',
          previewUrl: t.previewUrl,
        }))
        .filter((item) => {
          const isSameId = item.id === seed.id;
          if (isSameId) return false;

          const cleanFilterArtist = (
            cleanArtist(item.artist) || ''
          ).toLowerCase();
          const cleanFilterTitle = (cleanTitle(item.title) || '').toLowerCase();
          const cleanTargetArtist = (
            cleanArtist(seed.artist) || ''
          ).toLowerCase();
          const cleanTargetTitle = (cleanTitle(seed.title) || '').toLowerCase();

          return !(
            cleanFilterArtist === cleanTargetArtist &&
            cleanFilterTitle === cleanTargetTitle
          );
        })
        .slice(0, 8);

      setRecommendations(items);
      setIsSearchMode(false);
    } catch {
      setError('Failed to fetch recommendations.');
    } finally {
      setInitialLoading(false);
    }
  };

  // Handle clicking a search result - use the selected track directly as seed
  const handleSelectSearchResult = async (track: TrackInfo) => {
    try {
      setInitialLoading(true);
      setError(null);
      setSearchResults([]);
      setQuery('');

      // Set the clicked track as the seed
      setSeedTrack(track);

      // Fetch key matches for this track
      if (track.key) {
        try {
          const api = ApiService.getApiClient();
          const matches = await api.keysControllerGetKeyMatches({
            keyDto: {
              camelot: track.key,
              standard: '',
            },
          });
          setKeyMatches(matches);
        } catch {
          setKeyMatches(null);
        }
      }

      // Now fetch recommendations for this specific track
      const api = ApiService.getApiClient();
      const req: TrackControllerRecommendationSearchRequest = {
        spotifySearchDto: { term: `${track.artist} - ${track.title}` },
      };
      const resp = await api.trackControllerRecommendationSearch(req);

      if (resp && resp.length > 1) {
        // Skip the first result (the seed track) and map the rest as recommendations
        const items: Recommendation[] = resp
          .slice(1)
          .map((t: any) => ({
            id: t.id,
            title: t.name,
            artist: t.artists?.map((a: any) => a.name).join(', ') || '',
            albumArt: t.album?.imageUrl || '',
            bpm: Math.round(t.bpm || 0),
            key: t.keys?.camelot || '',
            previewUrl: t.previewUrl,
          }))
          .filter((item) => {
            const isSameId = item.id === track.id;
            if (isSameId) return false;

            const cleanFilterArtist = (
              cleanArtist(item.artist) || ''
            ).toLowerCase();
            const cleanFilterTitle = (
              cleanTitle(item.title) || ''
            ).toLowerCase();
            const cleanTargetArtist = (
              cleanArtist(track.artist) || ''
            ).toLowerCase();
            const cleanTargetTitle = (
              cleanTitle(track.title) || ''
            ).toLowerCase();

            return !(
              cleanFilterArtist === cleanTargetArtist &&
              cleanFilterTitle === cleanTargetTitle
            );
          })
          .slice(0, 8);

        setRecommendations(items);
      }

      setIsSearchMode(false);
    } catch (err) {
      console.error('Failed to select track:', err);
      setError('Failed to fetch recommendations.');
    } finally {
      setInitialLoading(false);
    }
  };

  const getKeyCategory = (key: string): KeyCategory | null => {
    if (!keyMatches || !key) return null;
    for (const category of Object.keys(keyMatches) as (keyof KeyMatches)[]) {
      const keys = keyMatches[category];
      if (Array.isArray(keys) && keys.includes(key)) {
        const formatted = category.charAt(0).toUpperCase() + category.slice(1);
        return formatted as KeyCategory;
      }
    }
    return null;
  };

  // Sort recommendations by key category priority
  const sortedRecommendations = useMemo(() => {
    return [...recommendations].sort((a, b) => {
      const catA = getKeyCategory(a.key);
      const catB = getKeyCategory(b.key);
      const orderA = catA ? keyCategoryOrder[catA] : 6;
      const orderB = catB ? keyCategoryOrder[catB] : 6;
      return orderA - orderB;
    });
  }, [recommendations, keyMatches]);

  useEffect(() => {
    if (sortedRecommendations.length > 0) {
      const isStillInList = sortedRecommendations.some(
        (rec) => rec.id === activeRecommendation?.ID,
      );
      if (!isStillInList) {
        const defaultRec = sortedRecommendations[0];
        const track: iTrack = {
          ID: defaultRec.id,
          Title: defaultRec.title,
          Artist: defaultRec.artist,
          AlbumCover: defaultRec.albumArt,
          PreviewURL: defaultRec.previewUrl || '',
          BPM: defaultRec.bpm,
          Key_Camelot: defaultRec.key,
          Key: defaultRec.key,
        };
        dispatch(setActiveRecommendation(track));
      }
    } else {
      if (activeRecommendation !== null) {
        dispatch(setActiveRecommendation(null));
      }
    }
  }, [sortedRecommendations, activeRecommendation, dispatch]);

  const handleRecClick = (rec: Recommendation) => {
    const track: iTrack = {
      ID: rec.id,
      Title: rec.title,
      Artist: rec.artist,
      AlbumCover: rec.albumArt,
      PreviewURL: rec.previewUrl || '',
      BPM: rec.bpm,
      Key_Camelot: rec.key,
      Key: rec.key,
    };

    if (activeRecommendation?.ID === rec.id) {
      dispatch(setSelectedTrack(track));
      history.push('/track');
    } else {
      dispatch(setActiveRecommendation(track));
    }
  };

  const handleRecDoubleClick = (rec: Recommendation) => {
    const track: iTrack = {
      ID: rec.id,
      Title: rec.title,
      Artist: rec.artist,
      AlbumCover: rec.albumArt,
      PreviewURL: rec.previewUrl || '',
      BPM: rec.bpm,
      Key_Camelot: rec.key,
      Key: rec.key,
    };
    dispatch(setSelectedTrack(track));
    history.push('/track');
  };

  const onSeedClick = () => {
    if (!seedTrack) return;
    const track: iTrack = {
      ID: seedTrack.id,
      Title: seedTrack.title,
      Artist: seedTrack.artist,
      AlbumCover: seedTrack.albumArt,
      PreviewURL: seedTrack.previewUrl || '',
      BPM: seedTrack.bpm,
      Key_Camelot: seedTrack.key,
      Key: seedTrack.key,
    };
    dispatch(setSelectedTrack(track));
    history.push('/track');
  };

  const clearSeedTrack = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSearchMode(true);
    setSeedTrack(null);
    setRecommendations([]);
    setKeyMatches(null);
    setSearchResults([]);
    setQuery('');
    setError(null);
    dispatch(setActiveRecommendation(null));
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const visibleRecommendations = sortedRecommendations.slice(0, 4);
  const showRecommendationSkeletons = initialLoading && !isSearchMode && !error;

  return (
    <IonCard className="modern-widget-card track-recommendations-card">
      <IonCardContent className="modern-widget-content">
        <div className="widget-header">
          <h3 className="widget-title">Track Recommendations</h3>
          {seedTrack && (
            <IonButton
              fill="clear"
              size="small"
              className="view-all-button"
              disabled={editing}
              onClick={onSeedClick}
            >
              All
            </IonButton>
          )}
        </div>

        {initialLoading && !seedTrack && !isSearchMode ? (
          <SeedSearchFieldSkeleton />
        ) : (
          <SeedSearchField
            seedTrack={seedTrack}
            query={query}
            isSearchMode={isSearchMode}
            isSearching={isSearching}
            searchResults={searchResults}
            loading={false}
            inputRef={searchInputRef}
            onQueryChange={(value) => {
              setQuery(value);
              if (error) setError(null);
            }}
            onSelectResult={handleSelectSearchResult}
            onClear={clearSeedTrack}
            onSeedClick={onSeedClick}
          />
        )}

        {error && (
          <div className="error-message">
            <span>{error}</span>
          </div>
        )}

        <div className="recommendations-content">
          {showRecommendationSkeletons && <WidgetListRowsSkeleton count={4} />}
          {seedTrack &&
            !showRecommendationSkeletons &&
            visibleRecommendations.map((rec) => {
              const keyCategory = getKeyCategory(rec.key);
              const isActive = activeRecommendation?.ID === rec.id;
              return (
                <button
                  key={rec.id}
                  type="button"
                  disabled={editing}
                  className={`recommendation-row${isActive ? ' is-active' : ''}`}
                  onClick={() => handleRecClick(rec)}
                  onDoubleClick={() => handleRecDoubleClick(rec)}
                >
                  <img src={rec.albumArt} alt="" />
                  <span className="recommendation-copy">
                    <span className="recommendation-title">{rec.title}</span>
                    <span className="recommendation-artist">{rec.artist}</span>
                  </span>
                  <span className="recommendation-meta">
                    <span className="recommendation-keyline">
                      <CamelotKeyChip value={rec.key} />
                      {rec.bpm > 0 && (
                        <span className="recommendation-bpm">
                          {rec.bpm}
                          <span className="bpm-unit"> bpm</span>
                        </span>
                      )}
                    </span>
                    {keyCategory && (
                      <span
                        className="recommendation-match"
                        style={{ color: keyCategoryColors[keyCategory] }}
                      >
                        {formatKeyCategory(keyCategory)}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
        </div>
      </IonCardContent>
    </IonCard>
  );
};
